import React, { useState } from 'react';
import {
    FormControl,
    FormHelperText,
    InputLeftAddon,
    Input,
    InputGroup,
    InputProps,
    Radio,
    Select,
    Checkbox,
    FormLabel,
    SelectProps,
    CheckboxProps,
    RadioProps,
    NumberInput,
    NumberInputField,
    forwardRef,
    NumberInputProps,
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { HelpButton, HelpButtonProps } from './HelpButton';

import { FaCalendarAlt, FaClock } from 'react-icons/fa';

type Props = {
    name: string;
    type?: string;
    label?: string;
    options?: { value: string; label: string }[];
    validationMessage?: string;
    isRequired?: boolean;
    onChange: (e: any) => void;
    onDateChange?: (dt: Date) => void;
    onNumberChange?: (valueAsString: string, valueAsNumber: number) => void;
    value: any;
    readOnly?: boolean;
    help?: HelpButtonProps;
    [x: string]: any;
};

export const IsOptionString = (obj: string[] | Object[]): obj is string[] => {
    return typeof obj === 'string';
};

const CustomValidationControl: React.FC<Props> = ({
    name,
    type = 'text',
    label,
    options,
    validationMessage = '',
    isRequired = false,
    onChange,
    onDateChange,
    onNumberChange,
    value,
    help,
    readOnly = false,
    ...rest
}) => {
    const [errorMessage, setErrorMessage] = useState('');
    const handleBlur = () => {
        if (!value && isRequired) {
            setErrorMessage(validationMessage);
        } else {
            setErrorMessage('');
        }
    };

    let inputControl;

    const restNoChilren = { ...rest };
    delete restNoChilren.children;
    const iProps = rest as InputProps;
    const numProps = rest as NumberInputProps;
    const checkProps = rest as CheckboxProps;
    const radioProps = restNoChilren as RadioProps;
    const selectProps = rest as SelectProps;
    const restChildren = rest.children;

    switch (type) {
        case 'text':
            inputControl = (
                <Input
                    name={name}
                    id={name}
                    onChange={onChange}
                    onBlur={handleBlur}
                    value={value}
                    readOnly={readOnly}
                    {...rest}
                />
            );
            break;
        case 'number':
            inputControl = (
                <NumberInput
                    name={name}
                    id={name}
                    value={value}
                    keepWithinRange={false}
                    clampValueOnBlur={false}
                    onChange={onChange}
                    inputMode={'numeric'}
                    precision={0}
                    type={'number'}
                    {...numProps}
                >
                    <NumberInputField></NumberInputField>
                </NumberInput>
            );
            break;
        case 'radio':
            inputControl = (
                <>
                    {options?.map((option) => {
                        return (
                            <Radio
                                key={option.value}
                                value={option.value}
                                name={option.value}
                                onChange={onChange}
                                onBlur={handleBlur}
                                checked={value === option.value}
                                {...radioProps}
                            >
                                {option}
                            </Radio>
                        );
                    })}
                </>
            );
            break;
        case 'select':
            inputControl = (
                <Select
                    name={name}
                    onChange={onChange}
                    onBlur={handleBlur}
                    value={value}
                    {...selectProps}
                >
                    {options?.map((option) => {
                        return (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        );
                    })}
                </Select>
            );
            break;
        case 'checkbox':
            inputControl = (
                <>
                    <Checkbox
                        name={name}
                        id={name}
                        onChange={onChange}
                        onBlur={handleBlur}
                        isChecked={value}
                        {...checkProps}
                    />
                    {restChildren}
                </>
            );
            break;

        case 'datetime':
            const CustomDateInput = (props: InputProps, ref: React.Ref<HTMLInputElement>) => {
                return (
                    <InputGroup>
                        <InputLeftAddon
                            children={<FaCalendarAlt color="gray.300" />}
                        ></InputLeftAddon>
                        <Input value={props.value} ref={ref} {...props}></Input>
                    </InputGroup>
                );
            };

            inputControl = (
                <DatePicker
                    customInput={React.createElement(forwardRef(CustomDateInput))}
                    name={name}
                    id={name}
                    selected={value}
                    onChange={(dt) => onDateChange && dt && onDateChange(dt)}
                    onBlur={handleBlur}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    timeCaption="time"
                    dateFormat="MMMM d, yyyy h:mm aa"
                ></DatePicker>
            );
            break;

        case 'time':
            const CustomInput = (props: InputProps, ref: React.Ref<HTMLInputElement>) => {
                return (
                    <InputGroup>
                        <InputLeftAddon children={<FaClock color="gray.300" />}></InputLeftAddon>
                        <Input value={props.value} ref={ref} {...props}></Input>
                    </InputGroup>
                );
            };
            inputControl = (
                <DatePicker
                    name={name}
                    id={name}
                    selected={value}
                    onChange={(dt) => onDateChange && dt && onDateChange(dt)}
                    onBlur={handleBlur}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    customInput={React.createElement(forwardRef(CustomInput))}
                />
            );
            break;
        default:
            inputControl = (
                <Input
                    name={name}
                    onChange={onChange}
                    onBlur={handleBlur}
                    value={value}
                    {...iProps}
                />
            );
            break;
    }

    return (
        <FormControl isInvalid={!!errorMessage || !!validationMessage} {...rest}>
            <>
                {label && (
                    <FormLabel htmlFor={name} mt={'0.5em'}>
                        {label}
                        {help && <HelpButton ml={2} onClick={help.onClick}></HelpButton>}
                    </FormLabel>
                )}
            </>

            {inputControl}

            {validationMessage && (
                <FormHelperText color={'#E53E3E'} id={`${name}-helper-text`}>
                    {validationMessage}
                </FormHelperText>
            )}
            {/* <FormErrorMessage></FormErrorMessage> */}
        </FormControl>
    );
};

export default CustomValidationControl;
